import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

export interface NavLink {
  href: string;
  label: string;
}

export interface NavGroup {
  title: string;
  links: NavLink[];
}

export interface LegalPageTemplateProps {
  /** Page title (h1 and document title) */
  title: string;
  /** Lead paragraph below the title */
  description: string;
  /** Meta description for SEO */
  metaDescription: string;
  /** Canonical path (e.g. "/privacy", "/terms") */
  canonicalPath: string;
  /** Optional "Last updated" text */
  lastUpdated?: string;
  /** Unique id for the main element (e.g. "terms-page", "privacy-page") */
  mainId: string;
  /** Sidebar navigation groups. Omit for a simple single-column layout. */
  sidebarNav?: NavGroup[];
  /** Main content (sections) */
  children: React.ReactNode;
}

const LegalPageTemplate: React.FC<LegalPageTemplateProps> = ({
  title,
  description,
  metaDescription,
  canonicalPath,
  lastUpdated,
  mainId,
  sidebarNav,
  children,
}) => {
  const { hash } = useLocation();
  const firstHref = sidebarNav?.[0]?.links?.[0]?.href;
  const isActive = (href: string) =>
    hash === href || (hash === '' && href === firstHref);

  const leadText = lastUpdated
    ? `${description} Last updated: ${lastUpdated}.`
    : description;

  return (
    <>
      <Helmet>
        <title>{title} | PH Therapy</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://www.pandhtherapy.co.uk${canonicalPath}`} />
      </Helmet>
      <Header />
      <main id={mainId} className="content-page">
        <div className="terms-page-layout">
          {sidebarNav && sidebarNav.length > 0 && (
            <aside className="terms-sidebar">
              <nav className="terms-nav" aria-label="Page contents">
                <h2 className="terms-nav-title">Contents</h2>
                {sidebarNav.map((group) => (
                  <React.Fragment key={group.title}>
                    <h3>{group.title}</h3>
                    <ul>
                      {group.links.map(({ href, label }) => (
                        <li key={href}>
                          <a
                            href={href}
                            className={isActive(href) ? 'active' : ''}
                          >
                            {label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ))}
              </nav>
            </aside>
          )}

          <div className="terms-content">
            <h1>{title}</h1>
            <p className="content-page-lead">{leadText}</p>
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LegalPageTemplate;
